import React, { useEffect, useState } from "react";
import Sidebar from "../../../../component/includes/sidebar";

import {
  Pencil,
  PlusCircle,
  Search,
  Trash2,
  Upload,
  X,
  AlertCircle,
} from "lucide-react";

import {
  CreateServiceData,
  Service,
  serviceClass,
  UpdateServiceData,
} from "../../../../services/Layanan/ListLayanan";

const DashboardService = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  
  const [previewImages, setPreviewImages] = useState<{
    mainImage: string | null;
    galleryImages: string[];
  }>({
    mainImage: null,
    galleryImages: [],
  });

  const [selectedFiles, setSelectedFiles] = useState<{
    mainImage: File | null;
    galleryImages: File[];
  }>({
    mainImage: null,
    galleryImages: [],
  });

  const [formData, setFormData] = useState<Partial<CreateServiceData>>({
    name: "",
    description: "",
    price: 0,
    phone_number: "",
  });

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Handle Image Preview
  const handleImagePreview = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "main" | "gallery"
  ) => {
    setImageError(null);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageError("Hanya file gambar yang diperbolehkan");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === "main") {
      setPreviewImages((prev) => ({ ...prev, mainImage: previewUrl }));
      setSelectedFiles((prev) => ({ ...prev, mainImage: file }));
    } else {
      if (previewImages.galleryImages.length >= 10) {
        setImageError("Maksimal 10 gambar galeri diperbolehkan");
        return;
      }
      setPreviewImages((prev) => ({
        ...prev,
        galleryImages: [...prev.galleryImages, previewUrl],
      }));
      setSelectedFiles((prev) => ({
        ...prev,
        galleryImages: [...prev.galleryImages, file],
      }));
    }
  };

  // Remove Image
  const removeImage = (type: "main" | "gallery", index?: number) => {
    if (type === "main") {
      setPreviewImages((prev) => ({ ...prev, mainImage: null }));
      setSelectedFiles((prev) => ({ ...prev, mainImage: null }));
    } else if (typeof index === "number") {
      setPreviewImages((prev) => ({
        ...prev,
        galleryImages: prev.galleryImages.filter((_, i) => i !== index),
      }));
      setSelectedFiles((prev) => ({
        ...prev,
        galleryImages: prev.galleryImages.filter((_, i) => i !== index),
      }));
    }
    setImageError(null);
  };

  // Fetch Services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await serviceClass.getAllServices();
        setServices(servicesData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load services");
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "price" ? parseFloat(value) || 0 : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: 0,
      phone_number: "",
    });
    setSelectedFiles({ mainImage: null, galleryImages: [] });
    setPreviewImages({ mainImage: null, galleryImages: [] });
    setEditingService(null);
    setImageError(null);
  };

  const openEditModal = (service: Service) => {
    setEditingService(service);

    setPreviewImages({
      mainImage: service.main_image
        ? `${process.env.REACT_APP_API_URL}${service.main_image}`
        : null,
      galleryImages: [], // Untuk sementara, galeri bisa dikembangkan nanti
    });

    setFormData({
      name: service.name,
      description: service.description || "",
      price: service.price,
      phone_number: service.phone_number || "",
    });

    setIsModalOpen(true);
  };

  const handleCreateService = async () => {
    if (!validateForm()) return;

    try {
      const createData: CreateServiceData = {
        ...formData,
        main_image: selectedFiles.mainImage!,
        gallery_image: selectedFiles.galleryImages,
      } as CreateServiceData;

      await serviceClass.createService(createData);
      resetForm();
      setIsModalOpen(false);
      alert("Layanan berhasil dibuat!");
      window.location.reload(); // sementara, nanti bisa diganti dengan refetch
    } catch (err) {
      alert(err instanceof Error ? err.message : "Gagal membuat layanan");
    }
  };

  const handleUpdateService = async () => {
    if (!editingService || !validateForm()) return;

    try {
      const updateData: UpdateServiceData = {
        id: editingService.id,
        ...formData,
      };

      if (selectedFiles.mainImage) {
        (updateData as any).main_image = selectedFiles.mainImage;
      }
      if (selectedFiles.galleryImages.length > 0) {
        (updateData as any).gallery_image = selectedFiles.galleryImages;
      }

      await serviceClass.updateService(updateData);
      resetForm();
      setIsModalOpen(false);
      alert("Layanan berhasil diperbarui!");
      window.location.reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Gagal memperbarui layanan");
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name?.trim()) {
      alert("Nama layanan tidak boleh kosong");
      return false;
    }
    if (!formData.description?.trim()) {
      alert("Deskripsi tidak boleh kosong");
      return false;
    }
    if (!formData.price || formData.price <= 0) {
      alert("Harga harus lebih dari 0");
      return false;
    }
    if (!formData.phone_number?.trim()) {
      alert("Nomor telepon tidak boleh kosong");
      return false;
    }
    if (!editingService && !selectedFiles.mainImage) {
      alert("Harap unggah gambar utama");
      return false;
    }
    return true;
  };

  const handleDeleteService = async (serviceId: number) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus layanan ini?")) {
      try {
        await serviceClass.deleteService(serviceId);
        alert("Layanan berhasil dihapus!");
        window.location.reload();
      } catch (err) {
        alert("Gagal menghapus layanan");
      }
    }
  };

  const filteredServices = services.filter((service) =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-500">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="flex flex-col md:flex-row">
        <Sidebar />
        <div className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-7xl mx-auto">
            {/* Header seperti di gambar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-2xl">📋</span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Service Management</h1>
                  <p className="text-gray-500">Manage your services</p>
                </div>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition shadow-md"
              >
                <PlusCircle size={20} />
                Add New Service
              </button>
            </div>

            {/* Search Bar */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder="Search services by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-5 py-3 pl-12 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
              />
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Table - Mirip Gambar */}
            <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-12">NO</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-28">MAIN IMAGE</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold">NAME</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-40">PRICE</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-48">PHONE NUMBER</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-44">CREATED AT</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-44">UPDATED AT</th>
                      <th className="px-6 py-4 text-left text-gray-600 font-semibold w-28">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredServices.map((service, index) => (
                      <tr key={service.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-5 text-center">
                          <div className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {index + 1}
                          </div>
                        </td>

                        <td className="px-6 py-5">
                          <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 flex items-center justify-center">
                            {service.main_image ? (
                              <img
                                src={`${process.env.REACT_APP_API_URL}${service.main_image}`}
                                alt={service.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-400 text-xs text-center px-2">No Image</span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-5 font-medium text-gray-800">
                          {service.name}
                        </td>

                        <td className="px-6 py-5 text-green-600 font-semibold">
                          Rp.{Number(service.price).toLocaleString("id-ID")}
                        </td>

                        <td className="px-6 py-5 text-gray-700 font-medium">
                          {service.phone_number || "-"}
                        </td>

                        <td className="px-6 py-5 text-gray-600">
                          {new Date(service.created_at).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-6 py-5 text-gray-600">
                          {new Date(service.updated_at).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-6 py-5">
                          <div className="flex gap-4">
                            <button
                              onClick={() => openEditModal(service)}
                              className="text-blue-500 hover:text-blue-600 transition"
                            >
                              <Pencil size={20} />
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              className="text-red-500 hover:text-red-600 transition"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal (Tetap sama seperti sebelumnya) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
            <button
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="absolute top-6 right-6 text-gray-500 hover:text-red-500"
            >
              <X size={28} />
            </button>

            <h2 className="text-3xl font-bold mb-8">
              {editingService ? "Edit Layanan" : "Tambah Layanan Baru"}
            </h2>

            {/* Form fields tetap sama seperti kode sebelumnya */}
            {/* ... (Nama, Deskripsi, Harga, No Telepon, Upload Gambar) ... */}

            <div className="grid grid-cols-1 gap-8">
              {/* Nama, Deskripsi, Harga, Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nama Layanan</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Masukkan nama layanan"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">No. Telepon</label>
                  <input
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Masukkan nomor telepon"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deskripsi</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Deskripsi layanan"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Harga</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Main Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gambar Utama <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImagePreview(e, "main")}
                      className="hidden"
                      id="main-upload"
                    />
                    <label htmlFor="main-upload" className="cursor-pointer">
                      <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600">
                        {previewImages.mainImage ? "Ganti Gambar Utama" : "Unggah Gambar Utama"}
                      </p>
                    </label>

                    {previewImages.mainImage && (
                      <div className="mt-6 relative inline-block">
                        <img
                          src={previewImages.mainImage}
                          alt="preview"
                          className="h-32 w-32 object-cover rounded-xl border"
                        />
                        <button
                          onClick={() => removeImage("main")}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Gallery Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gambar Galeri (Maks. 10)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImagePreview(e, "gallery")}
                      className="hidden"
                      id="gallery-upload"
                      disabled={previewImages.galleryImages.length >= 10}
                    />
                    <label htmlFor="gallery-upload" className="cursor-pointer">
                      <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600">
                        {previewImages.galleryImages.length >= 10
                          ? "Maksimal 10 gambar tercapai"
                          : `Unggah Gambar Galeri (${previewImages.galleryImages.length}/10)`}
                      </p>
                    </label>

                    <div className="grid grid-cols-5 gap-3 mt-6">
                      {previewImages.galleryImages.map((url, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={url}
                            alt={`gallery-${idx}`}
                            className="h-20 w-20 object-cover rounded-lg border"
                          />
                          <button
                            onClick={() => removeImage("gallery", idx)}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {imageError && (
                <div className="flex items-center gap-2 text-red-500">
                  <AlertCircle size={18} />
                  <span>{imageError}</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-10">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="px-8 py-3 text-gray-600 hover:bg-gray-100 rounded-xl transition"
              >
                Batal
              </button>
              <button
                onClick={editingService ? handleUpdateService : handleCreateService}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition"
              >
                <Upload size={18} />
                {editingService ? "Perbarui Layanan" : "Buat Layanan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardService;